import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Lock, Lightning } from '@phosphor-icons/react';
import axios from 'axios';
import { useCompany, hasFeature } from '../context/CompanyContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Wrap any page / section that requires a specific plan feature.
// Shows an in-app paywall card instead of the content when locked.
//
//   <FeatureGate feature="bom">
//     <BomPage />
//   </FeatureGate>
//
// If the caller provides `fallback`, we render that instead of the default
// paywall (useful for inline Upsell tiles).

export default function FeatureGate({ feature, children, fallback }) {
  const { company, loading } = useCompany();
  if (loading) return <div className="p-6 text-muted-foreground" data-testid="feature-gate-loading">Loading…</div>;
  // iter228 — Competition Demo tenants bypass every feature gate so judges
  // can experience the full product regardless of plan matrix.
  if (company?.is_demo) return children;
  // If company is null AFTER loading=false, the user has no tenant context
  // (superadmin outside their company, or a race on logout). Show paywall
  // rather than leaking gated pages.
  if (!company) {
    if (fallback) return fallback;
    return <Paywall feature={feature} company={null} />;
  }
  if (hasFeature(company, feature)) return children;
  if (fallback) return fallback;
  return <Paywall feature={feature} company={company} />;
}

export function Paywall({ feature, company }) {
  const pretty = (feature || '').replace(/_/g, ' ');
  const location = useLocation();
  // Beacon — fire-and-forget. Never blocks the UI.
  useEffect(() => {
    const ctrl = new AbortController();
    axios.post(
      `${API}/api/analytics/paywall-impression`,
      {
        feature,
        plan_key: company?.plan_key || null,
        path: location?.pathname || null,
        source: 'page_gate',
      },
      { withCredentials: true, signal: ctrl.signal },
    ).catch(() => { /* non-blocking */ });
    return () => ctrl.abort();
  }, [feature, company?.plan_key, location?.pathname]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-8" data-testid="feature-paywall">
      <div className="max-w-md text-center bg-card border-2 border-brand p-8 shadow-lg">
        <div className="w-14 h-14 bg-brand flex items-center justify-center mx-auto mb-4">
          <Lock size={26} weight="fill" className="text-white" />
        </div>
        <h2 className="text-2xl font-bold mb-2">
          Upgrade to unlock
        </h2>
        <p className="text-sm text-muted-foreground mb-1">
          <span className="font-mono uppercase font-bold text-brand">{pretty}</span> isn't included in your current plan.
        </p>
        <p className="text-xs text-muted-foreground font-mono uppercase tracking-widest mb-6">
          Current: {company?.plan_name || company?.plan_key || 'starter'}
        </p>
        <Link
          to="/billing"
          data-testid="paywall-upgrade-btn"
          className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-brand hover:bg-brand/90 text-white font-bold text-sm uppercase tracking-widest transition"
        >
          <Lightning size={16} weight="fill" />
          View plans & upgrade
        </Link>
      </div>
    </div>
  );
}

// Small inline badge: "Pro only" / "Enterprise" tag for locked sidebar items.
export function FeatureBadge({ feature, plan = 'pro' }) {
  const { company } = useCompany();
  if (!company) return null;
  if (hasFeature(company, feature)) return null;
  return (
    <span
      className="ml-auto text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 bg-slate-200 text-muted-foreground"
      data-testid={`feature-badge-${feature}`}
    >
      {plan}
    </span>
  );
}
